import React from 'react';
import { Users } from '@phosphor-icons/react';

interface CoacheeRow {
    engagementId: string | number;
    coacheeName?: string;
    coacheeUserUuid?: string;
    programName?: string;
    progress: number;
    status: string;
    completedSessions: number;
    totalSessions: number;
    totalGoals: number;
    avgGoalProgress: number;
    lastInteractionDate?: string;
}

interface CoachCoacheeSummaryWidgetProps {
    data?: CoacheeRow[] | null;
    loading?: boolean;
    error?: string;
}

const getInitials = (name: string): string =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

const timeAgo = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return '1d ago';
    if (days < 30) return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
};

export const CoachCoacheeSummaryWidget: React.FC<CoachCoacheeSummaryWidgetProps> = ({ data, loading, error }) => {
    const rows = Array.isArray(data) ? data : [];

    return (
        <div className="flex flex-col h-full">
            <div className="flex-1 overflow-auto px-4 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <Users className="w-8 h-8" />
                        <span className="text-sm">No coachees</span>
                    </div>
                ) : (
                    <div className="space-y-2.5">
                        {rows.map((row, idx) => {
                            const progress = Number(row.progress) || 0;
                            const avgGoal = Number(row.avgGoalProgress) || 0;
                            const sessions = `${Number(row.completedSessions) || 0}/${Number(row.totalSessions) || 0}`;
                            return (
                                <div key={row.engagementId || idx} className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 text-xs font-bold shrink-0">
                                        {row.coacheeName ? getInitials(row.coacheeName) : '??'}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-0.5">
                                            <span className="text-sm text-gray-800 truncate">{row.coacheeName || 'Unknown'}</span>
                                            <span className="text-[10px] text-gray-400 shrink-0 ml-2">{sessions} sessions</span>
                                        </div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-[10px] text-gray-400">{Number(row.totalGoals) || 0} goals (avg {avgGoal}%)</span>
                                            <span className="text-[10px] text-gray-300">&middot;</span>
                                            <span className="text-[10px] text-gray-400">{timeAgo(row.lastInteractionDate)}</span>
                                        </div>
                                        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full transition-all ${progress >= 60 ? 'bg-emerald-500' : progress >= 30 ? 'bg-amber-500' : 'bg-red-500'}`}
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
